import { useState } from "react"
import { Clock, TrendingUp, Shield, Lightbulb, Settings, Zap, ArrowRight } from "lucide-react"
import { DemoBookingModal } from "./demo-booking-modal"

const outcomes = [
  {
    icon: Clock,
    title: "Accelerate Time-to-Market",
    description: "Launch new integrations in days, not months. Our pre-built connectors eliminate custom API development.",
  },
  {
    icon: TrendingUp,
    title: "Improve Financial Outcomes",
    description: "Reduce integration costs by up to 70% compared to in-house development and maintenance.",
  },
  {
    icon: Shield,
    title: "Scale with Confidence",
    description: "Enterprise-grade infrastructure handles millions of transactions with guaranteed uptime.",
  },
  {
    icon: Settings,
    title: "Streamline Operations",
    description: "Eliminate manual data entry and reduce errors with automated healthcare data workflows.",
  },
  {
    icon: Lightbulb,
    title: "Security & Compliance",
    description: "HIPAA compliant platform with full audit trails, encryption, and access controls built-in.",
  },
  {
    icon: Zap,
    title: "Support Innovation",
    description: "Enable AI and analytics initiatives with clean, structured data from any healthcare source.",
  },
]

const stats = [
  { value: "70%", label: "Average Cost Savings", sublabel: "vs in-house builds" },
  { value: "4x", label: "Faster Time-to-Market", sublabel: "no-code deployment" },
  { value: "75%", label: "Time Saved", sublabel: "on financial close cycles" },
  { value: "Hours", label: "Average Deployment", sublabel: "not days or weeks" },
]

export function WorkflowSection() {
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  return (
    <section id="solutions" className="py-20 lg:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-4">
          <p className="text-[#9e3667] font-semibold text-sm uppercase tracking-wide">Outcomes That Matter</p>
        </div>
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold tracking-tight text-[#2d2d3a] sm:text-4xl">
            Transform Your Data Into{" "}
            <span className="bg-[linear-gradient(135deg,#a63083,#bf406a)] bg-clip-text text-transparent">Measurable ROI</span>
          </h2>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl p-6 shadow-sm border border-[#e8e8ec]">
              <p className="text-3xl lg:text-4xl font-bold bg-[linear-gradient(135deg,#a63083,#bf406a)] bg-clip-text text-transparent mb-1">{stat.value}</p>
              <p className="text-sm font-semibold text-[#2d2d3a]">{stat.label}</p>
              <p className="text-xs text-[#6b6b7b] mt-0.5">{stat.sublabel}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {outcomes.map((outcome) => (
            <div 
              key={outcome.title} 
              className="bg-white rounded-xl p-6 border border-[#e8e8ec] hover:border-[#9e3667]/40 hover:shadow-md transition-all"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#faf5f7] mb-4">
                <outcome.icon className="h-5 w-5 text-[#9e3667]" />
              </div>
              <h3 className="text-base font-semibold text-[#2d2d3a] mb-2">{outcome.title}</h3>
              <p className="text-sm text-[#6b6b7b] leading-relaxed">{outcome.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button 
            onClick={() => setIsDemoOpen(true)}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl border-2 border-[#9e3667] text-[#9e3667] font-medium text-lg transition-all hover:bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:border-transparent hover:text-white"
          >
            See How We Deliver ROI
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <DemoBookingModal open={isDemoOpen} onOpenChange={setIsDemoOpen} />
    </section>
  )
}
